import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, ShieldCheck, Sparkles, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const REDIRECT_DELAY_MS = 1800;

const HERO_IMG = "/logo.png";

export default function Inscripcion() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto redirect to register prefilled
  useEffect(() => {
    if (status === "success") {
      const params = new URLSearchParams({ name: form.name, email: form.email, phone: form.phone });
      const t = setTimeout(() => navigate(`/registro/abogado?${params.toString()}`), REDIRECT_DELAY_MS);
      return () => clearTimeout(t);
    }
  }, [status, form, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) return;
    setStatus("submitting");
    setErrorMsg("");
    try {
      await api.post("/early-access", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        source: new URLSearchParams(window.location.search).get("utm_source") || "inscripcion",
      });
      setStatus("success");
    } catch (err) {
      setErrorMsg(formatApiError(err.response?.data?.detail) || err.message);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="inscripcion-page">
      {/* Left column — visual + benefits */}
      <div className="relative bg-[hsl(var(--card))] hidden lg:flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 50% 50%, hsl(192 95% 52% / 0.3), transparent 60%)" }}/>
        <div className="relative z-10 flex flex-col items-center p-12">
          <img src={HERO_IMG} alt="Colega Link" className="w-64 h-64 rounded-2xl object-cover ring-1 ring-white/10"/>
          <div className="panel p-6 max-w-sm mt-10 backdrop-blur">
            <div className="label-eyebrow">Miembros Fundadores</div>
            <p className="serif text-2xl mt-2 leading-tight">Únete a los primeros 100 colegas.</p>
            <ul className="mt-4 space-y-2 text-sm">
              <li className="flex items-start gap-2"><BadgeCheck size={14} className="text-[hsl(var(--accent))] mt-0.5"/>3 meses gratis del plan Founder</li>
              <li className="flex items-start gap-2"><ShieldCheck size={14} className="text-[hsl(var(--accent))] mt-0.5"/>Validación manual de credenciales</li>
              <li className="flex items-start gap-2"><Sparkles size={14} className="text-[hsl(var(--accent))] mt-0.5"/>Prioridad en alertas geográficas</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Right column — form */}
      <div className="flex items-center justify-center p-8 sm:p-12 lg:p-16">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-baseline gap-2 lg:hidden mb-8" data-testid="brand-mobile">
            <span className="serif text-2xl font-semibold">Colega Link</span>
            <span className="label-eyebrow">Chile · Beta</span>
          </Link>

          {status !== "success" ? (
            <>
              <div className="label-eyebrow" data-testid="form-eyebrow">Inscripción · Miembros Fundadores</div>
              <h1 className="serif text-4xl md:text-5xl mt-3 leading-[1.05]">Completa tus datos y crea tu cuenta.</h1>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Te pedimos solo lo esencial. Después te llevamos a crear tu cuenta con los datos ya cargados.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="inscripcion-form">
                <Field label="Nombre completo">
                  <input
                    className="field" type="text" required autoComplete="name"
                    value={form.name} onChange={(e) => set("name", e.target.value)}
                    placeholder="Ej: María González"
                    data-testid="insc-name"
                  />
                </Field>
                <Field label="Correo electrónico">
                  <input
                    className="field" type="email" required autoComplete="email"
                    value={form.email} onChange={(e) => set("email", e.target.value)}
                    placeholder="tu@correo.cl"
                    data-testid="insc-email"
                  />
                </Field>
                <Field label="Teléfono (WhatsApp)">
                  <input
                    className="field" type="tel" required autoComplete="tel" inputMode="tel"
                    value={form.phone} onChange={(e) => set("phone", e.target.value)}
                    placeholder="+56 9 1234 5678"
                    data-testid="insc-phone"
                  />
                </Field>

                {status === "error" && (
                  <div className="text-sm text-[hsl(var(--destructive))]" data-testid="insc-error">{errorMsg}</div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="btn-primary w-full disabled:opacity-60"
                  data-testid="insc-submit"
                >
                  {status === "submitting" ? "Enviando…" : <>Unirme al grupo de WhatsApp <ArrowUpRight className="ml-2" size={16} /></>}
                </button>

                <p className="text-xs text-muted-foreground leading-relaxed pt-2">
                  Al enviar aceptas que contactemos por WhatsApp para validar tu inscripción.
                  Tus datos no se comparten con terceros.
                </p>
              </form>
            </>
          ) : (
            <div className="py-12" data-testid="insc-success">
              <CheckCircle2 size={56} className="text-[hsl(var(--accent))]"/>
              <h1 className="serif text-4xl md:text-5xl mt-6 leading-tight">¡Listo, {form.name.split(" ")[0]}!</h1>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Recibimos tus datos. Te estamos redirigiendo para crear tu cuenta…
              </p>
              <Link
                to={`/registro/abogado?name=${encodeURIComponent(form.name)}&email=${encodeURIComponent(form.email)}&phone=${encodeURIComponent(form.phone)}`}
                className="btn-primary mt-8 w-full"
                data-testid="insc-manual-redirect"
              >
                Continuar a crear cuenta <ArrowUpRight size={16} className="ml-2"/>
              </Link>
              <p className="text-xs text-muted-foreground mt-4 text-center">
                Si no te redirige en 2 segundos, haz clic en el botón.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
